import './style.css'
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import * as dat from 'dat.gui'
import { Vector3, VertexColors } from 'three'


// Debug
const gui = new dat.GUI({ width: 300 })

// Canvas
const canvas = document.querySelector('canvas.webgl')

// Scene
const scene = new THREE.Scene()

const galaxyParameters = {}
galaxyParameters.count = 500000
galaxyParameters.size = 0.0005
galaxyParameters.radius = 7
galaxyParameters.branches = 7
galaxyParameters.spin = 1.2
galaxyParameters.randomness = 0.9
galaxyParameters.randomnessPower = 6.7
galaxyParameters.centerColor = 0xff6800
galaxyParameters.outerColor = 0x0700ff
galaxyParameters.autoRotate = false
galaxyParameters.autoRotateSpeed = 1

let geometry = null
let material = null
let points = null

/**
 * Galaxy
 */
const generateGalaxy = () => {
        if (points !== null) {
            geometry.dispose()
            material.dispose()
            scene.remove(points)
        }


        geometry = new THREE.BufferGeometry()
        const positions = new Float32Array(galaxyParameters.count * 3)
        const colors = new Float32Array(galaxyParameters.count * 3)

        const colorCenter = new THREE.Color(galaxyParameters.centerColor)
        const colorOuter = new THREE.Color(galaxyParameters.outerColor)

        for (let i = 0; i < galaxyParameters.count; i++) {
            const i3 = i * 3

            // Position
            const radius = (Math.random() * galaxyParameters.radius)
            const spinAngle = radius * galaxyParameters.spin
            const branchAngle = ((i % galaxyParameters.branches) / galaxyParameters.branches * Math.PI * 2)

            const xRandomness = (Math.random() * galaxyParameters.randomness) //* (1 / Math.max(1, radius))
            const randomX = Math.pow(xRandomness, galaxyParameters.randomnessPower) * (Math.random() < 0.5 ? 1 : -1)

            const randomY = (Math.pow(Math.random() * galaxyParameters.randomness, galaxyParameters.randomnessPower)) * (Math.random() < 0.5 ? 1 : -1)

            const zRandomness = (Math.random() * galaxyParameters.randomness) // * (1 / Math.max(1, radius))
            const randomZ = Math.pow(zRandomness, galaxyParameters.randomnessPower) * (Math.random() < 0.5 ? 1 : -1)


            const point = new THREE.Vector3(
                Math.cos(branchAngle + spinAngle) * radius + randomX,
                randomY,
                Math.sin(branchAngle + spinAngle) * radius + randomZ
            )

            const distanceToCenter = point.distanceTo(new THREE.Vector3(0, 0, 0))

            positions[i3 + 0] = point.x
            positions[i3 + 1] = randomY * (1 / (distanceToCenter + 0.24))
            positions[i3 + 2] = point.z


            // Color
            const mixedColor = colorCenter.clone()
            mixedColor.lerp(colorOuter, radius / galaxyParameters.radius)
            colors[i3] = mixedColor.r
            colors[i3 + 1] = mixedColor.g
            colors[i3 + 2] = mixedColor.b
        }

        geometry.setAttribute(
            'position',
            new THREE.BufferAttribute(positions, 3)
        )

        geometry.setAttribute(
            'color',
            new THREE.BufferAttribute(colors, 3)
        )

        /**
         * Material
         */
        material = new THREE.PointsMaterial({
            size: galaxyParameters.size,
            sizeAttenuation: true,
            depthWrite: false,
            blending: THREE.AdditiveBlending,
            vertexColors: true
        })


        /**
         * Points
         */
        points = new THREE.Points(geometry, material)

        scene.add(points)

    }
    /**
     * Debug UI
     */
gui.close()
gui.add(galaxyParameters, 'count').min(1000).max(500000).step(1000).onFinishChange(generateGalaxy)
gui.add(galaxyParameters, 'size').min(0.001).max(0.1).step(0.001).onFinishChange(generateGalaxy)
gui.add(galaxyParameters, 'radius').min(0.01).max(20).step(0.01).onFinishChange(generateGalaxy)
gui.add(galaxyParameters, 'branches').min(3).max(20).step(1).onFinishChange(generateGalaxy)
gui.add(galaxyParameters, 'spin').min(-5).max(5).step(0.001).onFinishChange(generateGalaxy)
gui.add(galaxyParameters, 'randomness').min(0.5).max(1).step(0.001).onFinishChange(generateGalaxy)
gui.add(galaxyParameters, 'randomnessPower').min(1).max(10).step(0.001).onFinishChange(generateGalaxy)
gui.addColor(galaxyParameters, 'centerColor').onFinishChange(generateGalaxy)
gui.addColor(galaxyParameters, 'outerColor').onFinishChange(generateGalaxy)
gui.add(galaxyParameters, 'autoRotate').onChange(() => {
    controls.autoRotate = galaxyParameters.autoRotate
})
gui.add(galaxyParameters, 'autoRotateSpeed').min(0.1).max(2).step(0.1).onChange(() => {
    controls.autoRotateSpeed = galaxyParameters.autoRotateSpeed
})

generateGalaxy()

const starAmount = 10000
const starsGeometry = new THREE.BufferGeometry()
const starsMatieral = new THREE.PointsMaterial({ size: 0.01 })
const starPositions = new Float32Array(starAmount * 3)
for (let i = 0; i < starAmount; i++) {
    const i3 = i * 3

    starPositions[i3 + 0] = THREE.MathUtils.randFloat(-100, 100)
    starPositions[i3 + 1] = THREE.MathUtils.randFloat(-100, 100)
    starPositions[i3 + 2] = THREE.MathUtils.randFloat(-100, 100)

}
starsGeometry.setAttribute(
    'position',
    new THREE.BufferAttribute(starPositions, 3)
)
const stars = new THREE.Points(starsGeometry, starsMatieral)
scene.add(stars)


/**
 * Sizes
 */
const sizes = {
    width: window.innerWidth,
    height: window.innerHeight
}

window.addEventListener('resize', () => {
    // Update sizes
    sizes.width = window.innerWidth
    sizes.height = window.innerHeight

    // Update camera
    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()

    // Update renderer
    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
})

/**
 * Camera
 */
// Base camera
const camera = new THREE.PerspectiveCamera(30, sizes.width / sizes.height, 0.1, 100)
camera.position.x = 25
camera.position.y = 20
camera.position.z = 0
scene.add(camera)

// Controls
const controls = new OrbitControls(camera, canvas)
controls.enableDamping = true

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
    canvas: canvas
})
renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))

/**
 * Animate
 */
const clock = new THREE.Clock()
controls.autoRotate = galaxyParameters.autoRotate
controls.autoRotateSpeed = galaxyParameters.autoRotateSpeed
const tick = () => {
    const elapsedTime = clock.getElapsedTime()

    // Update controls
    controls.update()

    // Render
    renderer.render(scene, camera)

    // Call tick again on the next frame
    window.requestAnimationFrame(tick)

    geometry.rotateY(0.001)

    // camera.position.x = -Math.sin(elapsedTime)
    // camera.position.x = elapsedTime
    // camera.position.y = elapsedTime

}

tick()